import Button from '@mui/material/Button';
import { Icon } from '@shared/icons';
import { Translate } from '@shared/translation';
import type { ReactNode } from 'react';
import type { DateRange } from '../types';
import { useDateRangeTexts } from '../useDateRangeTexts';
import { getStepper } from './steppers';

interface StepButtonProps {
  onClick: (value: DateRange) => void;
  value: DateRange;
}

export function PrevButton({ onClick, value }: StepButtonProps) {
  const texts = useDateRangeTexts();
  const stepper = getStepper(value);

  return (
    <StepButton
      iconPosition="start"
      icon={<Icon name="chevron_left" size="xsmall" />}
      onClick={() => onClick(stepper.previous())}
    >
      <Translate text={texts.steppers.previous.label} />
    </StepButton>
  );
}

export function NextButton({ onClick, value }: StepButtonProps) {
  const texts = useDateRangeTexts();
  const stepper = getStepper(value);

  return (
    <StepButton
      iconPosition="end"
      icon={<Icon name="chevron_right" size="xsmall" />}
      onClick={() => onClick(stepper.next())}
    >
      <Translate text={texts.steppers.next.label} />
    </StepButton>
  );
}

interface BaseStepButtonProps {
  children: ReactNode;
  icon: ReactNode;
  iconPosition: 'start' | 'end';
  onClick: () => void;
}

function StepButton({
  children,
  icon,
  iconPosition,
  onClick,
}: BaseStepButtonProps) {
  return (
    <Button
      color="inherit"
      onClick={onClick}
      size="small"
      type="button"
      variant="outlined"
      {...(iconPosition === 'start' ? { startIcon: icon } : { endIcon: icon })}
      sx={(theme) => ({
        borderRadius: 16,
        fontSize: 11,
        fontWeight: 400,
        minHeight: 26,
        lineHeight: 'normal',
        padding: theme.spacing(0, 1),
        textTransform: 'none',
      })}
    >
      {children}
    </Button>
  );
}
